import React, { memo, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { useResponsiveTableContext } from '../ResponsiveTableContext';
import { TResponsiveTableRow } from '../types';
import { AccordionCell } from './AccordionCell';
import {
  responsiveTableActions,
  responsiveTableStore,
  useResponsiveTable,
} from '../store';
import { AccordionItem, IAccordionItemButton } from '@apia/components';

export interface IAccordionElement {
  row: TResponsiveTableRow;
  rowIndex: number;
}

const NoMemoAccordionElement = ({ row, rowIndex }: IAccordionElement) => {
  const { name } = useResponsiveTableContext();

  const indexColumns = useResponsiveTable((global) => {
    const tableState = global.responsiveTableSlice[name];
    return tableState?.accordionIndexColumns;
  }, shallowEqual);
  const columns = useResponsiveTable((global) => {
    const tableState = global.responsiveTableSlice[name];
    return tableState?.columns;
  }, shallowEqual);

  const title = React.useMemo(() => {
    if (row.title) return row.title;
    if (Array.isArray(indexColumns)) {
      return indexColumns
        .map(
          (current) =>
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            row.cells[current.index].title ||
            (row.cells[current.index].children as string),
        )
        .join(' | ');
    }
    return row.cells[0].title ?? (row.cells[0].children as string);
  }, [indexColumns, row]);
  const isSelected = useResponsiveTable((global) =>
    global.responsiveTableSlice[name].allowSelection !== false
      ? global.responsiveTableSlice[name].selectedRows.includes(rowIndex)
      : undefined,
  );
  const isFocused = useResponsiveTable(
    (global) => global.responsiveTableSlice[name].focusedRow === rowIndex,
  );
  const buttonProps = useMemo<IAccordionItemButton>(() => {
    const htmlTitles: boolean[] = [];

    const hasAccordionTitleColumn = columns.some((c) => c.showInAccordionTitle);

    row.cells.forEach((cell, i) => {
      if (
        (hasAccordionTitleColumn &&
          columns[i].showInAccordionTitle &&
          cell.isHtml) ||
        (!hasAccordionTitleColumn && i === 0 && cell.isHtml)
      ) {
        htmlTitles.push(true);
      }
    });

    return {
      title,
      ariaLabel: title,
      label: title,
      checked: isSelected,
      isHtml: htmlTitles,
      onChange: (isChecked: boolean) => {
        const isMultiple =
          responsiveTableStore.getState().responsiveTableSlice[name].isMultiple;
        responsiveTableStore.dispatch(
          responsiveTableActions.update({
            tableName: name,
            selectedRows: isMultiple
              ? isChecked
                ? [
                    ...responsiveTableStore.getState().responsiveTableSlice[
                      name
                    ].selectedRows,
                    rowIndex,
                  ]
                : responsiveTableStore
                    .getState()
                    .responsiveTableSlice[
                      name
                    ].selectedRows.filter((current) => current !== rowIndex)
              : [rowIndex],
          }),
        );
      },
      tabIndex: isFocused ? 0 : -1,
      rightButtons: row.accordionButtonProps?.rightButtons,
      onUserPressEnter: row.accordionButtonProps?.onUserPressEnter,
      domButtonProps: {
        buttonSx: {
          '&.accordion__item__button .accordion__item__button__label': {
            color: row.color,
          },
        },
      },
    };
  }, [
    columns,
    isFocused,
    isSelected,
    name,
    row.accordionButtonProps?.onUserPressEnter,
    row.accordionButtonProps?.rightButtons,
    row.cells,
    row.color,
    rowIndex,
    title,
  ]);

  if (!Array.isArray(indexColumns) || !columns) return null;

  return (
    <AccordionItem id={row.id} buttonProps={buttonProps}>
      {columns.map((currentColumn, columnIndex) => {
        const Renderer =
          row.cells[columnIndex].AccordionRenderer ??
          currentColumn.AccordionRenderer ??
          AccordionCell;

        return currentColumn.hideFromAccordion ||
          currentColumn.showAsAdditional ? null : (
          <Renderer
            cell={row.cells[columnIndex]}
            column={currentColumn}
            columnIndex={columnIndex}
            key={currentColumn.name}
            row={row}
            rowIndex={rowIndex}
          />
        );
      })}

      {columns
        .filter((current) => !!current.showAsAdditional)
        .map((currentColumn, columnIndex) => {
          const Renderer =
            row.cells[columnIndex].AccordionRenderer ??
            currentColumn.AccordionRenderer ??
            AccordionCell;

          return currentColumn.hideFromAccordion ? null : (
            <Renderer
              cell={row.cells[columnIndex]}
              column={currentColumn}
              columnIndex={columnIndex}
              key={currentColumn.name}
              row={row}
              rowIndex={rowIndex}
            />
          );
        })}
    </AccordionItem>
  );
};

export const AccordionElement = memo(NoMemoAccordionElement);
